/**
 * @file toast.ts
 *
 * Imperative toast helper for the Online Shop admin. Appends Gravity toast
 * elements to the `.gv-toast-container` node rendered by the app shell
 * (`index.tsx`), mirroring WSB-v2's `displayToast`.
 *
 * Gravity's React package (0.4.35) does not re-export a programmatic toast API
 * from its package root, so — like WSB-v2 — this drives the toast through the
 * DOM using Gravity's toast classes and `<gv-icon>` web component. Screens call
 * the typed {@link notifySuccess} / {@link notifyError} wrappers so success and
 * failure feedback stays consistent across every tab and is trivial to mock in
 * specs (`jest.mock('../feedback/toast')`). Variant icons and timings live in
 * {@link ./toast-constants}.
 */

import {
  TOAST_CLOSE_ICON,
  TOAST_DEFAULT_DURATION,
  TOAST_REMOVE_TRANSITION,
  TOAST_VARIANT_ICONS,
  TOAST_VISIBLE_DELAY,
  type ToastVariant,
} from './toast-constants';

/** Options for {@link displayToast}. */
export interface ToastOptions {
  /** Visual style. Defaults to `'success'`. */
  variant?: ToastVariant;
  /** Auto-dismiss delay in milliseconds. Defaults to `TOAST_DEFAULT_DURATION`. */
  duration?: number;
}

/**
 * Builds a single Gravity toast element (icon + message + close button).
 *
 * @param text - The (already-localized) message body.
 * @param variant - The visual style.
 *
 * @returns The detached toast element, ready to append.
 */
function buildToastElement(text: string, variant: ToastVariant): HTMLDivElement {
  const toast = document.createElement('div');
  toast.classList.add('gv-toast', `gv-toast-${variant}`);

  const icon = document.createElement('gv-icon');
  icon.className = 'gv-notice-icon';
  icon.setAttribute('aria-hidden', 'true');
  icon.setAttribute('src', TOAST_VARIANT_ICONS[variant]);

  const content = document.createElement('p');
  content.className = 'gv-toast-content';
  content.textContent = text;

  const close = document.createElement('button');
  close.type = 'button';
  close.className = 'gv-toast-close';
  close.setAttribute('aria-label', 'Close');
  const closeGlyph = document.createElement('gv-icon');
  closeGlyph.setAttribute('aria-hidden', 'true');
  closeGlyph.setAttribute('src', TOAST_CLOSE_ICON);
  close.appendChild(closeGlyph);

  toast.append(icon, content, close);
  return toast;
}

/**
 * Removes a toast with Gravity's exit transition, then detaches the node.
 *
 * @param container - The toast container.
 * @param toast - The toast element to remove.
 */
function removeToast(container: Element, toast: Element): void {
  toast.classList.add('gv-remove');
  setTimeout(() => {
    if (container.contains(toast)) {
      container.removeChild(toast);
    }
  }, TOAST_REMOVE_TRANSITION);
}

/**
 * Shows a transient toast notification. No-op when the toast container is
 * absent (e.g. a unit test that did not render the app shell).
 *
 * @param text - The (already-localized) message to display.
 * @param options - Optional variant and duration overrides.
 */
export function displayToast(text: string, options?: ToastOptions): void {
  const container = document.querySelector('.gv-toast-container');
  if (!container) {
    return;
  }

  const variant = options?.variant ?? 'success';
  const toast = buildToastElement(text, variant);
  container.appendChild(toast);

  // Defer the visible class one frame so Gravity's enter transition can run.
  setTimeout(() => {
    if (container.contains(toast)) {
      toast.classList.add('gv-visible');
    }
  }, TOAST_VISIBLE_DELAY);

  const autoRemove = setTimeout(
    () => removeToast(container, toast),
    options?.duration ?? TOAST_DEFAULT_DURATION,
  );

  const close = toast.querySelector('.gv-toast-close');
  close?.addEventListener('click', () => {
    clearTimeout(autoRemove);
    removeToast(container, toast);
  });
}

/**
 * Shows a success toast.
 *
 * @param message - The (already-localized) confirmation message.
 */
export function notifySuccess(message: string): void {
  displayToast(message, { variant: 'success' });
}

/**
 * Shows an error toast.
 *
 * @param message - The (already-localized) error message.
 */
export function notifyError(message: string): void {
  displayToast(message, { variant: 'alert' });
}
